import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ErrorState } from "../../shared/StateMessage";
import { getPromiseDetail } from "../../../api/hub/now";
import useFocusTrap from "../../../hooks/useFocusTrap";
import { BTN_PRIMARY, BTN_QUIET } from "./buttonStyles";

const UNAVAILABLE = "This promise isn't available.";
const HELP_LINE =
  "Done = we kept it. No longer needed = it was cancelled or doesn't apply anymore.";

// The status badge. Overdue reads red and Done green; every other status, and any the
// server adds later, reads neutral.
const BADGE_BASE = "shrink-0 rounded-md px-2 py-0.5 text-sm font-semibold";
const BADGE_TONE = {
  Overdue: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  Done: "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300",
};
const BADGE_NEUTRAL = "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";

/**
 * The grey "how it closed" line for a decided promise, or null for an open one.
 *
 * UNVERIFIED against live fn_promise_detail: the spec says only that a set `decided` reads
 * "Closed by AI: {reason}" or "Closed when its task was finished", and does not give its
 * shape. Assumed here: a string is shown as the server wrote it; an object with a non-empty
 * string `reason` is the AI case; any other set value is read as "closed when its task was
 * finished" only when the promise has a task, and otherwise as a bare "Closed", so an
 * unrecognised shape never asserts a cause the payload did not give. Reconcile with a real
 * closed promise.
 * @param {unknown} decided  `p.decided`
 * @param {unknown} [task]   `p.task`, the one other signal of how a promise closed
 * @returns {string|null}
 */
export function decidedLine(decided, task) {
  if (!decided) return null;
  if (typeof decided === "string") return decided.trim() ? decided : null;
  const reason = typeof decided.reason === "string" ? decided.reason.trim() : "";
  if (reason) return `Closed by AI: ${reason}`;
  return task ? "Closed when its task was finished" : "Closed";
}

/** A payload string, or "" for anything else: an unprobed field must not crash the render. */
const str = (v) => (typeof v === "string" ? v : "");

/** Whether `fn_promise_detail` answered with a promise (not null, an array, a scalar or a gate object). */
const isPromise = (p) =>
  p !== null && typeof p === "object" && !Array.isArray(p) && p.permitted !== false;

const DISABLED = "disabled:cursor-not-allowed disabled:opacity-50";
const LINK_CLASS = `${BTN_QUIET} inline-block shrink-0`;

/**
 * The slide-out detail for one promise (a `commitment` row on Now), loaded by
 * `fn_promise_detail`. Every string comes from the payload as the server wrote it — no
 * client-side date math — and the source email is shown in full. The three actions are
 * each rendered only when the payload's own flag allows it; what they do, and the task
 * form behind "Make it a task", belong to the page (the props below).
 *
 * It fetches on open and again whenever `reloadKey` changes. A response for a promise that
 * is no longer the open one is dropped. A refetch that fails or answers "unavailable" while a
 * promise is showing keeps it, with a note and a retry, and disables the actions (the write
 * that triggered the refetch already committed, so the flags on screen are out of date).
 *
 * @param {object} props
 * @param {string} props.promiseId                   The promise's id (the `fn_now` item id).
 * @param {string} [props.dismissLabel]              The row's server `dismiss_label`.
 * @param {number} [props.reloadKey]                 Bumped by the page to refetch the detail.
 * @param {() => void} props.onClose
 * @param {(p: object) => void} props.onDone
 * @param {(p: object) => void} props.onNoLongerNeeded
 * @param {(p: object) => void} props.onMakeTask
 * @param {() => void} [props.onRetryLink]           Present while a created task is still unlinked.
 * @param {boolean} [props.hideMakeTask]             Hides "Make it a task" (a task already exists, unlinked).
 * @param {boolean} [props.escDisabled]              Esc is the task form's while it is open.
 * @param {(p: object) => void} [props.onLoaded]     Called with the payload after each successful load; the page ends its busy state here.
 * @param {boolean} [props.taskLost]                 A task was made from this promise but its id never came back, so it cannot be linked.
 * @param {boolean} [props.busy]                     An action is in flight: the buttons disable.
 * @param {string|null} [props.error]                An action's refusal, shown above the buttons.
 */
export default function PromisePanel({
  promiseId,
  dismissLabel,
  reloadKey = 0,
  onClose,
  onDone,
  onNoLongerNeeded,
  onMakeTask,
  onRetryLink,
  hideMakeTask = false,
  escDisabled = false,
  taskLost = false,
  onLoaded,
  busy = false,
  error = null,
}) {
  const [state, setState] = useState({ status: "loading", p: null, message: null, stale: false });
  const [retryNonce, setRetryNonce] = useState(0);
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  // Tab stays inside the panel; while the task form is open it is the form's to trap.
  useFocusTrap(dialogRef, !escDisabled);

  // A different promise starts from the skeleton; a refetch of the same one keeps showing
  // what it has until the new payload lands.
  const shownIdRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    if (shownIdRef.current !== promiseId) {
      shownIdRef.current = promiseId;
      setState({ status: "loading", p: null, message: null, stale: false });
    }
    // A failed read keeps a promise that is already showing (flagged stale); with nothing
    // showing it is the error state.
    const failed = (message) =>
      setState((cur) =>
        cur.p ? { ...cur, stale: true } : { status: "error", p: null, message, stale: false },
      );
    (async () => {
      try {
        const p = await getPromiseDetail(promiseId);
        if (cancelled) return;
        if (!isPromise(p)) {
          failed(UNAVAILABLE);
          return;
        }
        setState({ status: "ready", p, message: null, stale: false });
        onLoadedRef.current?.(p);
      } catch (e) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("[PromisePanel] detail failed", e);
        failed(e?.message || UNAVAILABLE);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [promiseId, reloadKey, retryNonce]);

  useEffect(() => {
    closeRef.current?.focus();
  }, [promiseId]);

  // Containment holds wherever focus is: after an action the focused button is disabled or
  // gone and focus falls to the page, so the next Tab would land behind the panel. Any focus
  // that arrives outside the panel is sent to its close button. Released while the task form
  // is open (it is outside this element and owns the keyboard then). The release is read from
  // a ref set during render, not from the effect's dependencies: the form's autofocus runs in
  // the same commit that opens it, before an effect could swap the listener.
  const formOpenRef = useRef(escDisabled);
  formOpenRef.current = escDisabled;
  useEffect(() => {
    const pullBack = (e) => {
      if (formOpenRef.current) return;
      if (dialogRef.current && !dialogRef.current.contains(e.target)) closeRef.current?.focus();
    };
    document.addEventListener("focusin", pullBack);
    return () => document.removeEventListener("focusin", pullBack);
  }, []);

  // The task form closing hands focus back to the panel's close button.
  const formWasOpenRef = useRef(escDisabled);
  useEffect(() => {
    if (formWasOpenRef.current && !escDisabled) closeRef.current?.focus();
    formWasOpenRef.current = escDisabled;
  }, [escDisabled]);

  useEffect(() => {
    if (escDisabled) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    // Capture phase: the task form's own Esc closes it in the bubble phase, and the panel
    // must not pick up that same keypress once the form is gone.
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [escDisabled, onClose]);

  const retry = useCallback(() => {
    // Keep a promise that is showing; only an empty panel goes back to the skeleton.
    setState((cur) => (cur.p ? cur : { status: "loading", p: null, message: null, stale: false }));
    setRetryNonce((n) => n + 1);
  }, []);

  const { status, p, message, stale } = state;

  return (
    <>
      <div
        data-testid="promise-backdrop"
        aria-hidden="true"
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/40"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Promise"
        className="fixed inset-x-0 bottom-0 top-0 z-50 flex w-full flex-col overflow-y-auto border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900 md:inset-y-0 md:left-auto md:right-0 md:w-[600px] md:max-w-[calc(100vw-60px)] md:border-l"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3 dark:border-gray-800">
          <span className="text-sm font-semibold uppercase text-gray-500 dark:text-gray-400">Promise</span>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-lg leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
          >
            &times;
          </button>
        </div>

        <div className="flex-1 px-5 py-5">
          {status === "loading" && (
            <div role="status" aria-label="Loading promise" className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-6 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
              ))}
            </div>
          )}

          {status === "error" && (
            <ErrorState variant="inset" padding="p-0" message={message} onRetry={retry} />
          )}

          {status === "ready" && p && (
            <PromiseBody
              p={p}
              dismissLabel={dismissLabel}
              onDone={onDone}
              onNoLongerNeeded={onNoLongerNeeded}
              onMakeTask={onMakeTask}
              onRetryLink={onRetryLink}
              taskLost={taskLost}
              hideMakeTask={hideMakeTask}
              busy={busy || stale}
              stale={stale}
              onRefresh={retry}
              error={error}
            />
          )}
        </div>
      </div>
    </>
  );
}

function PromiseBody({
  p,
  dismissLabel,
  onDone,
  onNoLongerNeeded,
  onMakeTask,
  onRetryLink,
  taskLost,
  hideMakeTask,
  busy,
  stale,
  onRefresh,
  error,
}) {
  const threadId = str(p.conversation?.thread_id);
  const clientLine = [str(p.client?.name), str(p.promised_by) && `by ${str(p.promised_by)}`]
    .filter(Boolean)
    .join(" · ");
  const decided = decidedLine(p.decided, p.task);
  const dateLine = [str(p.due_label), str(p.promised_label)].filter(Boolean).join(" · ");
  const statusLabel = str(p.status_label);
  const sourceFrom = [str(p.source?.from), str(p.source?.at_label)].filter(Boolean).join(", ");
  const taskId = p.task?.id;
  const taskStatus = str(p.task?.status_label) || str(p.task?.status);

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-start justify-between gap-3">
          {/* The promise text is the server's, verbatim and often lower-case; only its
              first letter is shown capitalised (CSS), so the text itself is untouched. */}
          <h2 className="min-w-0 break-words text-lg font-semibold text-gray-900 first-letter:uppercase dark:text-gray-100">
            {str(p.what)}
          </h2>
          {statusLabel && (
            <span
              data-promise-status
              className={`${BADGE_BASE} ${BADGE_TONE[statusLabel] || BADGE_NEUTRAL}`}
            >
              {statusLabel}
            </span>
          )}
        </div>
        {dateLine && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{dateLine}</p>}
        {clientLine && <p className="text-sm text-gray-600 dark:text-gray-300">{clientLine}</p>}
      </div>

      {stale && (
        <div
          role="status"
          data-promise-stale
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200"
        >
          <span>Couldn&apos;t refresh this promise, so what is shown may be out of date.</span>
          <button type="button" onClick={onRefresh} className={BTN_QUIET}>
            Try again
          </button>
        </div>
      )}

      {p.source && typeof p.source === "object" && (
        <section data-promise-source className="rounded-lg border border-gray-200 p-4 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            {`From the email (${sourceFrom})`}
          </h3>
          <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-300">
            {str(p.source.text)}
          </p>
        </section>
      )}

      {taskId && (
        <div
          data-promise-task
          className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-3 text-sm dark:border-gray-800"
        >
          <span className="min-w-0 break-words text-gray-700 dark:text-gray-300">
            {`Task: ${str(p.task.title)}`}
            {taskStatus ? ` (${taskStatus})` : ""}
          </span>
          <Link to={`/hub/work?task=${encodeURIComponent(taskId)}`} className={LINK_CLASS}>
            Open task
          </Link>
        </div>
      )}

      {decided && (
        <p data-promise-decided className="text-sm text-gray-500 dark:text-gray-400">
          {decided}
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
        >
          <span className="min-w-0 break-words">{error}</span>
        </div>
      )}

      {taskLost && (
        <div
          role="alert"
          data-promise-lost
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
        >
          The task was created, but its id wasn&apos;t returned, so it couldn&apos;t be linked — find it on Work.
        </div>
      )}

      {onRetryLink && (
        <div
          data-promise-unlinked
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-700 dark:border-gray-800 dark:text-gray-300"
        >
          <span>A task was made from this promise, but it isn&apos;t linked to it yet.</span>
          <button type="button" disabled={busy} onClick={onRetryLink} className={`${BTN_QUIET} ${DISABLED}`}>
            Retry link
          </button>
        </div>
      )}

      <p className="text-sm text-gray-500 dark:text-gray-400">{HELP_LINE}</p>

      {/* Open conversation on the left, the three actions floated to its right; the row
          wraps on a phone, keeping the actions right-aligned. */}
      <div data-promise-actions className="flex flex-wrap items-center justify-between gap-2">
        {threadId && (
          <Link
            to={`/hub/conversations?thread=${encodeURIComponent(threadId)}`}
            className={LINK_CLASS}
          >
            Open conversation
          </Link>
        )}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {p.can_mark_done && (
            <button type="button" disabled={busy} onClick={() => onDone(p)} className={`${BTN_PRIMARY} ${DISABLED}`}>
              Done
            </button>
          )}
          {p.can_withdraw && (
            <button type="button" disabled={busy} onClick={() => onNoLongerNeeded(p)} className={`${BTN_QUIET} ${DISABLED}`}>
              {dismissLabel || "No longer needed"}
            </button>
          )}
          {p.can_make_task && !hideMakeTask && (
            <button type="button" disabled={busy} onClick={() => onMakeTask(p)} className={`${BTN_QUIET} ${DISABLED}`}>
              Make it a task
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
